import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Boxes, ArrowLeft, AlertTriangle, Trash2 } from "lucide-react";
import { getApiUrl, getAuthHeaders } from "../config/api";

// Utility function to format date without timezone conversion
const formatDate = (dateString) => {
  if (!dateString) return 'No date';
  // Split the date string and format it directly to avoid timezone issues
  const [year, month, day] = dateString.split('T')[0].split('-');
  return `${month}/${day}/${year}`;
};

export default function SuppliesOrderDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(null);
  const [vendorOptions, setVendorOptions] = useState([]);
  const [brandOptions, setBrandOptions] = useState([]);
  const [categoryOptions, setCategoryOptions] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      setError("");
      try {
        const res = await axios.get(getApiUrl(`/api/supplies/orders/${id}`), { headers: getAuthHeaders() });
        setData(res.data);
        setForm({
          vendorName: res.data.order.vendor_name || "",
          orderDate: res.data.order.order_date?.slice(0,10) || "",
          currency: res.data.order.currency || 'USD',
          shipping: Number(res.data.order.shipping ?? 0).toFixed(2),
          other_total: Number(res.data.order.other_total ?? 0).toFixed(2),
          tax_total: Number(res.data.order.tax_total ?? 0).toFixed(2),
          notes: res.data.order.notes || "",
          lines: res.data.lines.map(l => ({
            productName: l.product_name || "",
            brandName: l.brand_name || "",
            categoryName: l.category_name || "",
            qty: Number(l.qty ?? 0).toFixed(2),
            unit_desc: l.unit_desc || "",
            unit_price: Number(l.unit_price ?? 0).toFixed(2),
            tax_rate: Number(l.tax_rate ?? 0).toFixed(4),
            other_line_amt: Number(l.other_line_amt ?? 0).toFixed(2),
            notes: l.notes || ""
          }))
        });
        
        // Check if edit parameter is in URL
        const shouldEdit = searchParams.get('edit') === 'true';
        if (shouldEdit) {
          setEditing(true);
        }
      } catch (e) {
        setError(e.response?.data?.error || "Failed to load order");
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, [id, searchParams]);



  const currency = data?.order?.currency || 'USD';

  const updateHeader = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const updateLine = (idx, name, value) => {
    setForm(prev => {
      const lines = [...prev.lines];
      lines[idx] = { ...lines[idx], [name]: value };
      return { ...prev, lines };
    });
  };

  const fetchVendors = async (q) => {
    try {
      const res = await axios.get(getApiUrl('/api/supplies/lookups/vendors'), { headers: getAuthHeaders(), params: { q } });
      setVendorOptions(res.data || []);
    } catch {}
  };

  const fetchBrands = async (q) => {
    try {
      const res = await axios.get(getApiUrl('/api/supplies/lookups/brands'), { headers: getAuthHeaders(), params: { q } });
      setBrandOptions(res.data || []);
    } catch {}
  };

  const fetchCategories = async (q) => {
    try {
      const res = await axios.get(getApiUrl('/api/supplies/lookups/categories'), { headers: getAuthHeaders(), params: { q } });
      setCategoryOptions(res.data || []);
    } catch {}
  };

  const addLine = () => {
    setForm(prev => ({
      ...prev,
      lines: [...prev.lines, { productName: "", brandName: "", categoryName: "", qty: "1.00", unit_desc: "", unit_price: "0.00", tax_rate: "0.0000", other_line_amt: "0.00", notes: "" }]
    }));
  };

  const removeLine = (idx) => {
    setForm(prev => ({ ...prev, lines: prev.lines.filter((_, i) => i !== idx) }));
  };

  const save = async () => {
    try {
      await axios.put(getApiUrl(`/api/supplies/orders/${id}`), form, { headers: getAuthHeaders() });
      setEditing(false);
      // refresh
      const res = await axios.get(getApiUrl(`/api/supplies/orders/${id}`), { headers: getAuthHeaders() });
      setData(res.data);
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to save');
    }
  };

  return (
    <>
      <div className="p-4 sm:p-6">
        <div className="flex items-center gap-3 mb-4 sm:mb-6">
          <button onClick={() => navigate(-1)} className="mr-2 sm:mr-3 p-2 rounded hover:bg-gray-100"><ArrowLeft className="w-4 h-4 sm:w-5 sm:h-5" /></button>
          <h2 className="flex items-center text-lg sm:text-xl md:text-2xl font-semibold text-gray-800">
            <Boxes className="mr-2 text-blue-600 w-5 h-5 sm:w-6 sm:h-6" /> Purchase Details
            </h2>
        </div>

        <div className="bg-white rounded-lg shadow p-3 sm:p-4">
          {error && <div className="mb-3 p-3 rounded bg-red-50 text-red-700 text-sm">{error}</div>}
          {isLoading || !data ? (
            <p className="text-gray-600">Loading...</p>
          ) : (
            <div>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4 mb-4 text-sm">
                <div>
                  <div className="text-gray-500">Vendor</div>
                  {editing ? (
                    <>
                      <input name="vendorName" value={form.vendorName} onChange={(e)=>{ updateHeader(e); fetchVendors(e.target.value); }} list="vendor-list-detail" className="w-full border rounded px-3 py-2" />
                      <datalist id="vendor-list-detail">
                        {vendorOptions.map(v => (<option key={v.id} value={v.name} />))}
                      </datalist>
                    </>
                  ) : (
                    <div className="font-medium">{data.order.vendor_name}</div>
                  )}
                </div>
                <div>
                  <div className="text-gray-500">Order Date</div>
                  {editing ? (
                    <input type="date" name="orderDate" value={form.orderDate} onChange={updateHeader} className="w-full border rounded px-3 py-2" />
                  ) : (
                    <div className="font-medium">{formatDate(data.order.order_date)}</div>
                  )}
                </div>
                <div className="hidden">
                  <div className="text-gray-500">Currency</div>
                  {editing ? (
                    <input name="currency" value={form.currency} onChange={updateHeader} className="w-full border rounded px-3 py-2" />
                  ) : (
                    <div className="font-medium">{data.order.currency}</div>
                  )}
                </div>
              </div>

              <div className="overflow-x-auto">
                <table className="min-w-full text-xs sm:text-sm">
                  <thead>
                    <tr className="text-left text-gray-600 border-b">
                      <th className="py-2 pr-2">Product</th>
                      <th className="py-2 pr-2 hidden sm:table-cell">Brand</th>
                      <th className="py-2 pr-2 hidden md:table-cell">Category</th>
                      <th className="py-2 pr-2 text-right">Qty</th>
                      <th className="py-2 pr-2 hidden sm:table-cell">Unit</th>
                      <th className="py-2 pr-2 text-right">Price</th>
                      <th className="py-2 pr-2 text-right hidden lg:table-cell">Tax Rate</th>
                      <th className="py-2 pr-2 text-right hidden lg:table-cell">Other</th>
                      <th className="py-2 pr-2 text-right hidden md:table-cell">Subtotal</th>
                      <th className="py-2 pr-2 text-right hidden md:table-cell">Tax</th>
                      <th className="py-2 pr-2 text-right">Total</th>
                      {editing && <th className="py-2 pr-2"></th>}
                    </tr>
                  </thead>
                  <tbody>
                    {(editing && form ? form.lines : data.lines).map((ln, idx) => {
                      const qty = Number(editing ? ln.qty : ln.qty).valueOf();
                      const unitPrice = Number(editing ? ln.unit_price : ln.unit_price).valueOf();
                      const otherLineAmt = Number(editing ? ln.other_line_amt : ln.other_line_amt).valueOf();
                      const taxRate = Number(editing ? ln.tax_rate : ln.tax_rate).valueOf();
                      const lineSubtotal = (qty || 0) * (unitPrice || 0) + (otherLineAmt || 0);
                      const lineTax = lineSubtotal * (taxRate || 0);
                      const lineTotal = lineSubtotal + lineTax;
                      return (
                        <React.Fragment key={editing ? idx : ln.id}>
                          {/* Mobile: Product name on its own row */}
                          <tr className="border-b sm:hidden">
                            <td colSpan="4" className="py-2 pr-2">
                              <div className="flex justify-between items-center">
                                <div className="flex-1">
                                  <div className="font-medium">{editing && form ? <input value={ln.productName} onChange={(e)=>updateLine(idx,'productName',e.target.value)} className="w-full border rounded px-2 py-1" /> : ln.product_name}</div>
                                  <div className="text-xs text-gray-500">
                                    {ln.brand_name && `${ln.brand_name}`}
                                    {ln.category_name && ` • ${ln.category_name}`}
                                  </div>
                                </div>
                                {editing && (
                                  <button onClick={() => removeLine(idx)} className="p-1 text-red-600 hover:bg-red-50 rounded ml-2">
                                    <Trash2 className="w-4 h-4" />
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                          
                          {/* Mobile: Details row */}
                          {editing && form ? (
                            // Mobile edit mode - show all fields
                            <>
                              <tr className="border-b sm:hidden">
                                <td colSpan="4" className="py-2 pr-2">
                                  <div className="space-y-3">
                                    {/* Brand and Category */}
                                    <div className="grid grid-cols-2 gap-2">
                                      <div>
                                        <label className="text-xs text-gray-500 block mb-1">Brand</label>
                                        <div>
                                          <input value={ln.brandName} onChange={(e)=>{updateLine(idx,'brandName',e.target.value); fetchBrands(e.target.value);}} list={`brand-list-detail-${idx}`} className="w-full border rounded px-2 py-1 text-sm" placeholder="Brand" />
                                          <datalist id={`brand-list-detail-${idx}`}>{brandOptions.map(b => (<option key={b.id} value={b.name} />))}</datalist>
                                        </div>
                                      </div>
                                      <div>
                                        <label className="text-xs text-gray-500 block mb-1">Category</label>
                                        <div>
                                          <input value={ln.categoryName} onChange={(e)=>{updateLine(idx,'categoryName',e.target.value); fetchCategories(e.target.value);}} list={`category-list-detail-${idx}`} className="w-full border rounded px-2 py-1 text-sm" placeholder="Category" />
                                          <datalist id={`category-list-detail-${idx}`}>{categoryOptions.map(c => (<option key={c.id} value={c.name} />))}</datalist>
                                        </div>
                                      </div>
                                    </div>
                                    
                                    {/* Qty, Unit, Price */}
                                    <div className="grid grid-cols-3 gap-2">
                                      <div>
                                        <label className="text-xs text-gray-500 block mb-1">Qty</label>
                                        <input value={ln.qty} onChange={(e)=>updateLine(idx,'qty',e.target.value)} className="w-full border rounded px-2 py-1 text-sm text-right" placeholder="0" />
                                      </div>
                                      <div>
                                        <label className="text-xs text-gray-500 block mb-1">Unit</label>
                                        <input value={ln.unit_desc} onChange={(e)=>updateLine(idx,'unit_desc',e.target.value)} className="w-full border rounded px-2 py-1 text-sm" placeholder="each" />
                                      </div>
                                      <div>
                                        <label className="text-xs text-gray-500 block mb-1">Price</label>
                                        <input value={ln.unit_price} onChange={(e)=>updateLine(idx,'unit_price',e.target.value)} className="w-full border rounded px-2 py-1 text-sm text-right" placeholder="0.00" />
                                      </div>
                                    </div>
                                    
                                    {/* Tax Rate and Other Amount */}
                                    <div className="grid grid-cols-2 gap-2">
                                      <div>
                                        <label className="text-xs text-gray-500 block mb-1">Tax Rate</label>
                                        <input value={ln.tax_rate} onChange={(e)=>updateLine(idx,'tax_rate',e.target.value)} className="w-full border rounded px-2 py-1 text-sm text-right" placeholder="0.07" />
                                      </div>
                                      <div>
                                        <label className="text-xs text-gray-500 block mb-1">Other Amount</label>
                                        <input value={ln.other_line_amt} onChange={(e)=>updateLine(idx,'other_line_amt',e.target.value)} className="w-full border rounded px-2 py-1 text-sm text-right" placeholder="0.00" />
                                      </div>
                                    </div>
                                    
                                    {/* Totals */}
                                    <div className="grid grid-cols-3 gap-2 pt-2 border-t">
                                      <div className="text-center">
                                        <div className="text-xs text-gray-500">Subtotal</div>
                                        <div className="text-sm font-medium">{lineSubtotal.toLocaleString(undefined, { style: 'currency', currency, minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                                      </div>
                                      <div className="text-center">
                                        <div className="text-xs text-gray-500">Tax</div>
                                        <div className="text-sm font-medium">{lineTax.toLocaleString(undefined, { style: 'currency', currency, minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                                      </div>
                                      <div className="text-center">
                                        <div className="text-xs text-gray-500">Total</div>
                                        <div className="text-sm font-bold">{lineTotal.toLocaleString(undefined, { style: 'currency', currency, minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                                      </div>
                                    </div>
                                  </div>
                                </td>
                              </tr>
                            </>
                          ) : (
                            // Mobile view mode - simplified display
                            <>
                              <tr className="border-b sm:hidden">
                                <td className="py-1 pr-2 text-xs text-gray-500">Qty</td>
                                <td className="py-1 pr-2 text-xs text-gray-500">Price</td>
                                <td className="py-1 pr-2 text-xs text-gray-500">Total</td>
                                <td className="py-1 pr-2"></td>
                              </tr>
                              <tr className="border-b sm:hidden">
                                <td className="py-1 pr-2 text-right">{Number(ln.qty).toFixed(2)}</td>
                                <td className="py-1 pr-2 text-right">{Number(ln.unit_price).toLocaleString(undefined, { style: 'currency', currency, minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                                <td className="py-1 pr-2 text-right font-medium">{Number(ln.line_total).toLocaleString(undefined, { style: 'currency', currency, minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                                <td className="py-1 pr-2"></td>
                              </tr>
                            </>
                          )}

                          {/* Desktop: Original table row */}
                          <tr className="border-b hidden sm:table-row">
                            <td className="py-2 pr-2">
                              <div className="flex flex-col">
                                <div className="font-medium">{editing && form ? <input value={ln.productName} onChange={(e)=>updateLine(idx,'productName',e.target.value)} className="w-full border rounded px-2 py-1" /> : ln.product_name}</div>
                                <div className="text-xs text-gray-500 sm:hidden">
                                  {ln.brand_name && `${ln.brand_name}`}
                                  {ln.category_name && ` • ${ln.category_name}`}
                                </div>
                              </div>
                            </td>
                            <td className="py-2 pr-2 hidden sm:table-cell">{editing && form ? <><input value={ln.brandName} onChange={(e)=>{updateLine(idx,'brandName',e.target.value); fetchBrands(e.target.value);}} list={`brand-list-detail-${idx}`} className="w-full border rounded px-2 py-1" /><datalist id={`brand-list-detail-${idx}`}>{brandOptions.map(b => (<option key={b.id} value={b.name} />))}</datalist></> : (ln.brand_name || '-')}</td>
                            <td className="py-2 pr-2 hidden md:table-cell">{editing && form ? <><input value={ln.categoryName} onChange={(e)=>{updateLine(idx,'categoryName',e.target.value); fetchCategories(e.target.value);}} list={`category-list-detail-${idx}`} className="w-full border rounded px-2 py-1" /><datalist id={`category-list-detail-${idx}`}>{categoryOptions.map(c => (<option key={c.id} value={c.name} />))}</datalist></> : (ln.category_name || '-')}</td>
                            <td className="py-2 pr-2 text-right">{editing && form ? <input value={ln.qty} onChange={(e)=>updateLine(idx,'qty',e.target.value)} className="w-16 sm:w-24 border rounded px-2 py-1 text-right" /> : Number(ln.qty).toFixed(2)}</td>
                            <td className="py-2 pr-2 hidden sm:table-cell">{editing && form ? <input value={ln.unit_desc} onChange={(e)=>updateLine(idx,'unit_desc',e.target.value)} className="w-full border rounded px-2 py-1" /> : (ln.unit_desc || '-')}</td>
                            <td className="py-2 pr-2 text-right">{editing && form ? <input value={ln.unit_price} onChange={(e)=>updateLine(idx,'unit_price',e.target.value)} className="w-20 sm:w-28 border rounded px-2 py-1 text-right" /> : Number(ln.unit_price).toLocaleString(undefined, { style: 'currency', currency, minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                            <td className="py-2 pr-2 text-right hidden lg:table-cell">{editing && form ? <input value={ln.tax_rate} onChange={(e)=>updateLine(idx,'tax_rate',e.target.value)} className="w-16 border rounded px-2 py-1 text-right" placeholder="0.07" /> : `${(Number(ln.tax_rate) * 100).toFixed(1)}%`}</td>
                            <td className="py-2 pr-2 text-right hidden lg:table-cell">{editing && form ? <input value={ln.other_line_amt} onChange={(e)=>updateLine(idx,'other_line_amt',e.target.value)} className="w-20 border rounded px-2 py-1 text-right" placeholder="0.00" /> : Number(ln.other_line_amt).toLocaleString(undefined, { style: 'currency', currency, minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                            <td className="py-2 pr-2 text-right hidden md:table-cell">{editing && form ? lineSubtotal.toLocaleString(undefined, { style: 'currency', currency, minimumFractionDigits: 2, maximumFractionDigits: 2 }) : Number(ln.line_subtotal).toLocaleString(undefined, { style: 'currency', currency, minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                            <td className="py-2 pr-2 text-right hidden md:table-cell">{editing && form ? lineTax.toLocaleString(undefined, { style: 'currency', currency, minimumFractionDigits: 2, maximumFractionDigits: 2 }) : Number(ln.line_tax).toLocaleString(undefined, { style: 'currency', currency, minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                            <td className="py-2 pr-2 text-right font-medium">{editing && form ? lineTotal.toLocaleString(undefined, { style: 'currency', currency, minimumFractionDigits: 2, maximumFractionDigits: 2 }) : Number(ln.line_total).toLocaleString(undefined, { style: 'currency', currency, minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                            {editing && (
                              <td className="py-2 pr-2 text-right">
                                <button onClick={() => removeLine(idx)} className="px-2 py-1 text-red-600 hover:bg-red-50 rounded text-xs">Remove</button>
                              </td>
                            )}
                          </tr>
                          {/* Notes row */}
                          {(ln.notes || (editing && form)) && (
                            <tr className="border-b bg-gray-50">
                              <td colSpan="4" className="py-2 pr-2 sm:hidden">
                                <div className="flex items-center">
                                  <span className="text-sm text-gray-600 mr-2 font-medium">Notes:</span>
                                  {editing && form ? (
                                    <input
                                      value={ln.notes}
                                      onChange={(e)=>updateLine(idx,'notes',e.target.value)}
                                      className="flex-1 border rounded px-2 py-1 text-sm"
                                      placeholder="Optional line item notes..."
                                    />
                                  ) : (
                                    <span className="text-sm text-gray-800">{ln.notes || 'No notes'}</span>
                                  )}
                                </div>
                              </td>
                              <td colSpan={editing ? "9" : "8"} className="py-2 pr-2 hidden sm:table-cell">
                                <div className="flex items-center">
                                  <span className="text-sm text-gray-600 mr-2 font-medium">Notes:</span>
                                  {editing && form ? (
                                    <input
                                      value={ln.notes}
                                      onChange={(e)=>updateLine(idx,'notes',e.target.value)}
                                      className="flex-1 border rounded px-2 py-1 text-sm"
                                      placeholder="Optional line item notes..."
                                    />
                                  ) : (
                                    <span className="text-sm text-gray-800">{ln.notes || 'No notes'}</span>
                                  )}
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4 text-sm">
                <div>
                  <div className="text-gray-500 text-xs sm:text-sm">Subtotal</div>
                  <div className="font-medium text-sm sm:text-base">{Number(data.order.subtotal).toLocaleString(undefined, { style: 'currency', currency })}</div>
                </div>
                <div>
                  <div className="text-gray-500 text-xs sm:text-sm">Tax Total</div>
                  {editing ? (
                    <input 
                      name="tax_total" 
                      value={form.tax_total} 
                      onChange={updateHeader} 
                      className="w-full border rounded px-2 sm:px-3 py-1 sm:py-2 text-sm" 
                      placeholder="0.00"
                    />
                  ) : (
                    <div className="font-medium text-sm sm:text-base">{Number(data.order.tax_total).toLocaleString(undefined, { style: 'currency', currency })}</div>
                  )}
                </div>
                <div>
                  <div className="text-gray-500 text-xs sm:text-sm">Shipping</div>
                  {editing ? (
                    <input 
                      name="shipping" 
                      value={form.shipping} 
                      onChange={updateHeader} 
                      className="w-full border rounded px-2 sm:px-3 py-1 sm:py-2 text-sm" 
                      placeholder="0.00"
                    />
                  ) : (
                    <div className="font-medium text-sm sm:text-base">{Number(data.order.shipping).toLocaleString(undefined, { style: 'currency', currency, minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                  )}
                </div>
                <div>
                  <div className="text-gray-500 text-xs sm:text-sm">Other Total</div>
                  {editing ? (
                    <input 
                      name="other_total" 
                      value={form.other_total} 
                      onChange={updateHeader} 
                      className="w-full border rounded px-2 sm:px-3 py-1 sm:py-2 text-sm" 
                      placeholder="0.00"
                    />
                  ) : (
                    <div className="font-medium text-sm sm:text-base">{Number(data.order.other_total).toLocaleString(undefined, { style: 'currency', currency, minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                  )}
                </div>
              </div>
              
              <div className="mt-3 sm:mt-4 p-3 sm:p-4 bg-blue-50 rounded-lg">
                <div className="flex justify-between items-center">
                  <div className="text-gray-700 text-sm sm:text-base font-medium">Grand Total</div>
                  <div className="font-bold text-lg sm:text-xl text-blue-900">{Number(data.order.grand_total).toLocaleString(undefined, { style: 'currency', currency })}</div>
                </div>
              </div>

              <div className="mt-4 flex flex-col sm:flex-row items-stretch sm:items-center justify-end gap-2">
                {!editing ? (
                  <button onClick={() => setEditing(true)} className="px-4 py-2 border rounded w-full sm:w-auto">Edit</button>
                ) : (
                  <>
                    <button onClick={() => { setEditing(false); // reset from data
                      setForm({
                        vendorName: data.order.vendor_name || "",
                        orderDate: data.order.order_date?.slice(0,10) || "",
                        currency: data.order.currency || 'USD',
                        shipping: Number(data.order.shipping ?? 0).toFixed(2),
                        other_total: Number(data.order.other_total ?? 0).toFixed(2),
                        tax_total: Number(data.order.tax_total ?? 0).toFixed(2),
                        notes: data.order.notes || "",
                        lines: data.lines.map(l => ({
                          productName: l.product_name || "",
                          brandName: l.brand_name || "",
                          categoryName: l.category_name || "",
                          qty: Number(l.qty ?? 0).toFixed(2),
                          unit_desc: l.unit_desc || "",
                          unit_price: Number(l.unit_price ?? 0).toFixed(2),
                          tax_rate: Number(l.tax_rate ?? 0).toFixed(4),
                          other_line_amt: Number(l.other_line_amt ?? 0).toFixed(2),
                          notes: l.notes || ""
                        }))
                      });
                    }} className="px-4 py-2 border rounded w-full sm:w-auto">Cancel</button>
                    <button onClick={addLine} className="px-4 py-2 border rounded w-full sm:w-auto">Add Line</button>
                    <button onClick={save} className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded w-full sm:w-auto">Save</button>
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

    </>
  );
}


